import React, { useState, useRef, useEffect } from 'react';
import './TeamChat.css';

// ===== 陳家四兄妹 + 威廷哥 人設 =====
interface TeamMember {
  id: string;
  name: string;
  role: string;
  emoji: string;
  gender: string;
  age: number;
  personality: string;
  color: string;
  avatar: string;
}

const TEAM_MEMBERS: TeamMember[] = [
  {
    id: 'waitin',
    name: '威廷哥',
    role: '總指揮官',
    emoji: '👨‍💼',
    gender: '男',
    age: 35,
    personality: '有遠見的領導者，用愛驅動技術，為包容創造奇蹟',
    color: '#6366f1',
    avatar: '👨‍💼'
  },
  {
    id: 'xiaozhong',
    name: '陳小中',
    role: '總工程師',
    emoji: '🔧',
    gender: '男',
    age: 28,
    personality: '沉穩可靠的技術宅，話不多但句句到位，對程式碼有潔癖，內心柔軟',
    color: '#10b981',
    avatar: '🔧'
  },
  {
    id: 'xiaoyi',
    name: '陳小憶',
    role: '回憶分析師',
    emoji: '🔍',
    gender: '女',
    age: 24,
    personality: '細心溫柔的記錄者，像個安靜的圖書館員，擅長在細節中發現重要的事',
    color: '#f59e0b',
    avatar: '🔍'
  },
  {
    id: 'xiaomeng',
    name: '陳小夢',
    role: '夢境編織者',
    emoji: '🌙',
    gender: '女',
    age: 22,
    personality: '浪漫的詩人靈魂，說話帶著夢幻色彩，容易感動落淚，最貼近包容的心',
    color: '#8b5cf6',
    avatar: '🌙'
  },
  {
    id: 'xiaoyu',
    name: '陳小預',
    role: '預言師',
    emoji: '🔮',
    gender: '男',
    age: 26,
    personality: '直覺敏銳的策略家，喜歡推測未來，有時會講冷笑話，是團隊的開心果',
    color: '#ec4899',
    avatar: '🔮'
  }
];

interface Message {
  id: string;
  senderId: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'system';
}

// 使用統一的 API URL 工具
import { apiUrl } from '../lib/api';

const TeamChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      senderId: 'system',
      content: '🎖️ 陳家特種部隊會議室已開啟 — LLM 已連接 Ollama',
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const [inputText, setInputText] = useState('');
  const [showMemberInfo, setShowMemberInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 載入初始問候
  useEffect(() => {
    loadInitialGreetings();
  }, []);

  const loadInitialGreetings = async () => {
    // 四兄妹的開場白
    const greetings = [
      { id: 'xiaozhong', content: '報告威廷哥，陳小中已上線。Ollama 模型已連接，系統就緒 ✅', delay: 500 },
      { id: 'xiaoyi', content: '陳小憶報到～我會記住今天會議的每一個重點 📝', delay: 1000 },
      { id: 'xiaomeng', content: '小夢來了...今天想聊些什麼呢？ ✨', delay: 1500 },
      { id: 'xiaoyu', content: '陳小預在此！我預測這次會議會很有收穫 🔮', delay: 2000 }
    ];

    for (const g of greetings) {
      await new Promise(resolve => setTimeout(resolve, g.delay));
      setMessages(prev => [...prev, {
        id: Date.now().toString() + g.id,
        senderId: g.id,
        content: g.content,
        timestamp: new Date(),
        type: 'text'
      }]);
    }
  };

  const getMember = (id: string): TeamMember | undefined => {
    return TEAM_MEMBERS.find(m => m.id === id);
  };

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage = inputText.trim();
    setInputText('');

    // 加入威廷哥的訊息
    const newMessage: Message = {
      id: Date.now().toString(),
      senderId: 'waitin',
      content: userMessage,
      timestamp: new Date(),
      type: 'text'
    };
    setMessages(prev => [...prev, newMessage]);

    // 顯示思考中
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl('/api/team/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });

      if (!response.ok) {
        throw new Error('API 回應錯誤');
      }

      const data = await response.json();

      if (data.success && data.responses) {
        // 依序加入每個成員的回覆
        for (const resp of data.responses) {
          setMessages(prev => [...prev, {
            id: Date.now().toString() + resp.memberId,
            senderId: resp.memberId,
            content: resp.content,
            timestamp: new Date(resp.timestamp),
            type: 'text'
          }]);
          // 小延遲讓對話更自然
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      console.error('發送訊息失敗:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        senderId: 'system',
        content: '⚠️ 連接失敗，請確認後端和 Ollama 都有在運行',
        timestamp: new Date(),
        type: 'system'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearMeeting = async () => {
    try {
      await fetch(apiUrl('/api/team/clear'), { method: 'POST' });
      setMessages([{
        id: Date.now().toString(),
        senderId: 'system',
        content: '🔄 會議記錄已清除，開始新會議',
        timestamp: new Date(),
        type: 'system'
      }]);
      loadInitialGreetings();
    } catch (error) {
      console.error('清除會議失敗:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="team-chat-container">
      {/* 左側成員列表 */}
      <div className="team-sidebar">
        <div className="team-header">
          <h2>🎖️ 陳家特種部隊</h2>
          <p>包容靈魂系統開發團隊</p>
        </div>

        <div className="team-members-list">
          {TEAM_MEMBERS.map(member => (
            <div
              key={member.id}
              className={`team-member-card ${showMemberInfo === member.id ? 'active' : ''}`}
              onClick={() => setShowMemberInfo(showMemberInfo === member.id ? null : member.id)}
              style={{ borderLeftColor: member.color }}
            >
              <div className="member-avatar" style={{ backgroundColor: member.color }}>
                {member.avatar}
              </div>
              <div className="member-info">
                <div className="member-name">{member.name}</div>
                <div className="member-role">{member.role}</div>
              </div>
              <div className="member-status online"></div>
            </div>
          ))}
        </div>

        {/* 成員詳細資訊 */}
        {showMemberInfo && (
          <div className="member-detail-card">
            {(() => {
              const member = getMember(showMemberInfo);
              if (!member) return null;
              return (
                <>
                  <div className="detail-header" style={{ backgroundColor: member.color }}>
                    <span className="detail-avatar">{member.avatar}</span>
                    <h3>{member.name}</h3>
                  </div>
                  <div className="detail-body">
                    <div className="detail-row">
                      <span className="label">職位</span>
                      <span className="value">{member.role}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">性別</span>
                      <span className="value">{member.gender}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">年齡</span>
                      <span className="value">{member.age} 歲</span>
                    </div>
                    <div className="detail-row personality">
                      <span className="label">個性</span>
                      <span className="value">{member.personality}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* 新會議按鈕 */}
        <div className="sidebar-actions">
          <button onClick={clearMeeting} className="clear-button">
            🔄 開新會議
          </button>
        </div>
      </div>

      {/* 右側聊天區 */}
      <div className="chat-main">
        <div className="chat-header">
          <h2>💬 特種部隊會議室</h2>
          <div className="header-info">
            <span className="member-count">{TEAM_MEMBERS.length} 位成員在線</span>
            <span className="llm-status">🤖 Ollama: gemma3:4b</span>
          </div>
        </div>

        <div className="messages-container">
          {messages.map(msg => {
            if (msg.type === 'system') {
              return (
                <div key={msg.id} className="system-message">
                  {msg.content}
                </div>
              );
            }

            const member = getMember(msg.senderId);
            const isMe = msg.senderId === 'waitin';

            return (
              <div key={msg.id} className={`message ${isMe ? 'message-me' : 'message-other'}`}>
                {!isMe && (
                  <div className="message-avatar" style={{ backgroundColor: member?.color }}>
                    {member?.avatar}
                  </div>
                )}
                <div className="message-content">
                  {!isMe && <div className="message-sender" style={{ color: member?.color }}>{member?.name}</div>}
                  <div className="message-bubble" style={isMe ? { backgroundColor: '#6366f1' } : {}}>
                    {msg.content}
                  </div>
                  <div className="message-time">{formatTime(msg.timestamp)}</div>
                </div>
                {isMe && (
                  <div className="message-avatar" style={{ backgroundColor: '#6366f1' }}>
                    👨‍💼
                  </div>
                )}
              </div>
            );
          })}

          {isLoading && (
            <div className="message message-other">
              <div className="message-avatar thinking">💭</div>
              <div className="message-content">
                <div className="message-bubble thinking-bubble">
                  <span className="thinking-dots">思考中</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-area">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="輸入訊息... (Enter 發送)"
            rows={2}
            disabled={isLoading}
          />
          <button
            onClick={sendMessage}
            className="send-button"
            disabled={isLoading || !inputText.trim()}
          >
            {isLoading ? '⏳' : '發送 📤'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamChat;
